// src/hooks/useMessages.js
import { useEffect, useState, useRef } from "react";
import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    limit as fbLimit,
    getDocs,
    doc,
    getDoc
} from "firebase/firestore";

/**
 * useMessages (conversation-aware)
 *
 * - db: firestore instance
 * - auth: optional (unused)
 * - currentUid: authenticated uid (must be truthy)
 * - opts:
 *    - limitResults (number) default 100
 *    - includeLastItem (bool) default false  -> fetch latest item from each conversation's items subcollection
 *    - includePartnerProfiles (bool) default false -> fetch /users/{partnerUid} for display name/avatar
 *
 * Returns: { conversations, loading, error }
 */
export default function useMessages(db, auth, currentUid, opts = {}) {
    const limitResults = typeof opts.limitResults === "number" ? opts.limitResults : 100;
    const includeLastItem = !!opts.includeLastItem;
    const includePartnerProfiles = !!opts.includePartnerProfiles;

    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const snapRef = useRef(null);

    useEffect(() => {
        setConversations([]);
        setError(null);
        setLoading(true);

        if (!db) {
            const err = new Error("Firestore `db` missing");
            console.error("[useMessages] error:", err);
            setError(err);
            setLoading(false);
            return () => { };
        }

        if (!currentUid) {
            // not signed in (or waiting) — idle
            setLoading(false);
            return () => { };
        }

        let unsub = null;
        try {
            const convsCol = collection(db, "messages");

            // NOTE: avoid composite-index requirement by NOT ordering server-side.
            // We'll order client-side by lastUpdated (desc) after receiving results.
            const q = query(convsCol, where("participants", "array-contains", currentUid), fbLimit(limitResults));

            unsub = onSnapshot(
                q,
                async (snap) => {
                    snapRef.current = snap;
                    const rawConvs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

                    // Helper to derive basic fields from conv doc
                    const deriveConv = (c) => {
                        const participants = Array.isArray(c.participants) ? c.participants : [];
                        let partnerUid = null;
                        if (participants.length === 2) partnerUid = participants.find((p) => p !== currentUid) || null;
                        if (!partnerUid && c.lastMessage) {
                            partnerUid = c.lastMessage.from === currentUid ? c.lastMessage.to : c.lastMessage.from;
                        }
                        partnerUid = partnerUid || null;

                        const last = c.lastMessage || c.lastItem || null;
                        const preview = last?.text || (c.preview || "");
                        const ts = last?.createdAt || c.lastUpdated || null;
                        let unreadCount = 0;
                        if (c.unreadCounts && typeof c.unreadCounts === "object") {
                            const val = c.unreadCounts[currentUid];
                            unreadCount = Number.isInteger(val) ? val : 0;
                        } else if (last && last.to === currentUid && last.read === false) {
                            unreadCount = 1;
                        }
                        return {
                            id: c.id,
                            participants,
                            partnerUid,
                            lastMessage: c.lastMessage || null,
                            lastItem: c.lastItem || null,
                            lastUpdated: c.lastUpdated || null,
                            preview,
                            ts,
                            unreadCount,
                            _raw: c
                        };
                    };

                    // derive and then client-side sort by lastUpdated desc (nulls go last)
                    let convs = rawConvs.map(deriveConv).sort((a, b) => {
                        const ta = a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0;
                        const tb = b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0;
                        return tb - ta;
                    });

                    // Optionally fetch latest item from items subcollection for each conversation
                    if (includeLastItem && convs.length) {
                        try {
                            const withItems = await Promise.all(
                                convs.map(async (c) => {
                                    try {
                                        // Single-field orderBy on createdAt for subcollection; no composite index needed.
                                        const itemsQ = query(collection(db, "messages", c.id, "items"), orderBy("createdAt", "desc"), fbLimit(1));
                                        const itemsSnap = await getDocs(itemsQ);
                                        if (!itemsSnap.empty) {
                                            const d = itemsSnap.docs[0];
                                            const item = { id: d.id, ...d.data() };
                                            const merged = { ...c, lastItem: item };
                                            merged.preview = item.text || merged.preview;
                                            merged.ts = item.createdAt || merged.ts;
                                            if (!merged.unreadCount) {
                                                merged.unreadCount = item.to === currentUid && item.read === false ? 1 : merged.unreadCount;
                                            }
                                            return merged;
                                        }
                                    } catch (e) {
                                        console.warn("[useMessages] failed to fetch last item for conv", c.id, e.message || e);
                                    }
                                    return c;
                                })
                            );
                            convs = withItems;
                        } catch (e) {
                            console.warn("[useMessages] includeLastItem failed:", e);
                        }
                    }

                    // Optionally fetch partner profiles
                    if (includePartnerProfiles && convs.length) {
                        try {
                            const uniquePartners = Array.from(new Set(convs.map((c) => c.partnerUid).filter(Boolean)));
                            const profilePromises = uniquePartners.map(async (uid) => {
                                try {
                                    const uref = doc(db, "users", uid);
                                    const snap = await getDoc(uref);
                                    return { uid, data: snap.exists() ? snap.data() : null };
                                } catch (e) {
                                    console.warn("[useMessages] failed to fetch profile for", uid, e.message || e);
                                    return { uid, data: null };
                                }
                            });

                            const profiles = await Promise.all(profilePromises);
                            const profileMap = {};
                            profiles.forEach((p) => {
                                if (p && p.uid) profileMap[p.uid] = p.data;
                            });

                            convs = convs.map((c) => ({ ...c, partnerProfile: c.partnerUid ? profileMap[c.partnerUid] || null : null }));
                        } catch (e) {
                            console.warn("[useMessages] includePartnerProfiles failed:", e);
                        }
                    }

                    setConversations(convs);
                    setLoading(false);
                },
                (err) => {
                    console.error("[useMessages] conversation snapshot error:", err.code, err.message);
                    if (err && err.code === "failed-precondition") {
                        // Likely a missing index or similar; surface friendly message
                        setError(new Error("Query requires a Firestore composite index. Create the suggested index in Firebase Console or remove server-side ordering."));
                    } else if (err && err.code === "permission-denied") {
                        setError(new Error("Permission denied reading conversations. Check Firestore rules and that conversation docs include participants."));
                    } else {
                        setError(err);
                    }
                    setConversations([]);
                    setLoading(false);
                }
            );
        } catch (err) {
            console.error("[useMessages] setup error:", err);
            setError(err);
            setLoading(false);
            return () => { };
        }

        return () => {
            try {
                unsub && unsub();
            } catch (e) {
                // swallow
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [db, auth, currentUid, limitResults, includeLastItem, includePartnerProfiles]);

    return { conversations, loading, error };
}
